import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import ModalComponent from '@components/ModalComponent';
import Button from '@components/legacy/core/Button';
import { renderTextWithLineBreaks } from '@helpers/helpers';
import './index.less';
import Icon from '../Icon';

const normalizeMessage = (message) => {
    if (message == null || message === '') {
        return [];
    }
    if (Array.isArray(message)) {
        return message.filter(Boolean);
    }
    return [message];
};

const ConfirmationDialog = ({
    id,
    show = true,
    title,
    subtitle,
    icon,
    message,
    confirmQuestion,
    cancelLabel,
    confirmLabel,
    confirmVariant = 'danger',
    onCancel,
    onConfirm,
    onClosed,
    className,
}) => {
    const [isOpen, setIsOpen] = useState(show);
    const confirmedRef = useRef(false);

    useEffect(() => {
        setIsOpen(show);
    }, [show]);

    const handleModalClosed = () => {
        if (!confirmedRef.current) {
            onCancel?.();
        }
        setIsOpen(false);
        onClosed?.(confirmedRef.current);
        confirmedRef.current = false;
    };

    const handleDismiss = () => {
        confirmedRef.current = false;
        setIsOpen(false);
    };

    const handleConfirm = () => {
        confirmedRef.current = true;
        onConfirm?.();
        setIsOpen(false);
    };

    const messageParagraphs = normalizeMessage(message);
    const closeLabel = cancelLabel;

    return (
        <ModalComponent
            show={isOpen}
            closeModal={handleModalClosed}
            id={id}
            className="confirmation-dialog-modal"
        >
            <div className={classNames('overlay overlay-narrow confirmation-dialog', className)}>
                <div className="overlay-header">
                    <span
                        className="overlay-cancel overlay-cancel-mark"
                        role="button"
                        tabIndex={0}
                        aria-label={closeLabel}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleDismiss();
                            }
                        }}
                    />
                    <div className="confirmation-dialog__title-block">
                        {icon ? (
                            <div className="confirmation-dialog__title-icon">
                                <Icon value={icon} />
                            </div>
                        ) : null}
                        <div className="confirmation-dialog__title-text">
                            {title ? <h3 className="confirmation-dialog__title">{title}</h3> : null}
                            {subtitle ? <p className="confirmation-dialog__subtitle">{subtitle}</p> : null}
                        </div>
                    </div>
                </div>

                {(messageParagraphs.length > 0 || confirmQuestion) && (
                    <div className="overlay-main no-vertical-padding">
                        {messageParagraphs.map((paragraph, index) => (
                            <div key={index} className="confirmation-dialog__message">
                                {renderTextWithLineBreaks(paragraph)}
                            </div>
                        ))}
                        {confirmQuestion ? (
                            <p className="confirmation-dialog__question">{confirmQuestion}</p>
                        ) : null}
                    </div>
                )}

                <div className="overlay-footer">
                    {cancelLabel ? (
                        <Button type="secondary" className="confirmation-dialog__cancel" onClick={handleDismiss}>
                            {cancelLabel}
                        </Button>
                    ) : null}
                    {confirmLabel ? (
                        <Button
                            type="primary"
                            className={classNames('confirmation-dialog__confirm', {
                                'confirmation-dialog__confirm--danger': confirmVariant === 'danger',
                            })}
                            onClick={handleConfirm}
                        >
                            {confirmLabel}
                        </Button>
                    ) : null}
                </div>
            </div>
        </ModalComponent>
    );
};

export default ConfirmationDialog;
